import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { Tab } from '@modules/common/types/tab';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { NotebookListStateService } from '@modules/notes/services/notebook-list-state.service';
import { NotesListStateService } from '@modules/notes/services/notes-list-state.service';
import { NotesAppStateService } from '@modules/notes/services/state.service';
import { type ListState } from '@modules/notes/types/list-state';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { NotebooksFilters } from '@modules/notes/types/notebooks-filters';
import { NotesFilters } from '@modules/notes/types/notes-filters';
import { VirtualFolder } from '@modules/notes/types/virtual-folder';
import { isCreationAllowedInVirtualFolder } from '@modules/common/utils/virtual-folder';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-notes-panel',
  templateUrl: './notes-panel.component.html',
  styleUrls: ['./notes-panel.component.less'],
  standalone: false,
  providers: [
    ...NotesListStateService.providers({
      stateKey: StateKey.notesDPListState,
      withAdvancedSearch: true,
    }),
    ...NotebookListStateService.providers({
      stateKey: StateKey.notebooksDPListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class NotesPanelComponent implements OnInit, OnDestroy {
  public notebooksFilters: NotebooksFilters;
  public notesFilters: NotesFilters;
  public contextMenuOpened: boolean;
  public selectedFilter: VirtualFolder;
  public listState: ListState;
  public tabs: Tab[] = [
    { title: 'Notebooks', value: 'notebooks' },
    { title: 'Notes', value: 'notes' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'notes';

  /**
   * Derived pages (Flagged/Follow Up/Snoozed/Archive/Trash) offer no creation form — DP-001. Derived
   * from the stored page rather than from `selectFilter`, so it cannot drift from the list's filters.
   */
  protected readonly creationAllowed: Signal<boolean>;

  protected readonly popoverClose = new Subject<void>();
  private alive = new Subject<void>();

  private readonly notesListStateService = inject(NotesListStateService, { self: true });
  private readonly notebookListStateService = inject(NotebookListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  /** The panel's own Notes section: where its quick form creates (#2051). */
  protected readonly notesTenantScope = inject(NotesTenantScopeService, { optional: true });
  private readonly spaceContextStore = inject(SpaceContextStore);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  /** Location badges are shown on note rows only while an advanced search is applied. */
  protected readonly isAdvancedSearchApplied: Signal<boolean>;
  /**
   * Search-shaped: the shared query/markers plus transient ids. Notes exposes no form-only fields of its own,
   * so there is nothing extra to report here; the date range and `stitched` light the funnel pill instead.
   */
  protected readonly isSearchApplied: Signal<boolean>;

  constructor(
    private dpService: DynamicPanelService,
    private modalService: ModalService,
    private injector: Injector,
    private searchService: AdvancedSearchService,
    private stateService: NotesAppStateService,
  ) {
    this.isAdvancedSearchApplied = toSignal(this.searchService.isAdvancedSearchApplied(Application.notes), {
      initialValue: false,
    });
    this.isSearchApplied = toSignal(this.searchService.isSearchApplied(Application.notes), { initialValue: false });
    this.creationAllowed = toSignal(
      this.stateService.getVirtualFolder().pipe(map((folder) => isCreationAllowedInVirtualFolder(folder))),
      { initialValue: true },
    );
  }

  public ngOnInit() {
    // Inside a team an untouched panel lists the TEAM's notes, as the Contacts and Tasks panels do (#2016). Stored
    // before the lists subscribe: an empty slot would hold the panel's own team stream until the identity timeout.
    const scope = this.notesTenantScope;
    const team =
      this.spaceContextStore.type() === SpaceType.SHARED_SPACE ? this.spaceContextStore.contextId() : undefined;
    if (team && scope && scope.tenantId() === undefined) {
      scope.selectTenant(team);
    }

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
      });

    combineLatest([
      this.notebookListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.notebooksFilters = new NotebooksFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.notesListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        this.notesFilters = new NotesFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  public ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleNewNote(note = new Note()) {
    this.modalService.openFullForm(note, this.injector);
  }

  openNoteInModal(note: Note) {
    this.modalService.openFullForm(note, this.injector);
  }

  openNote(note: Note) {
    this.dpService.setFormItem(note);
  }

  openNotebook(notebook: Notebook) {
    this.dpService.setFormItem(notebook);
  }

  selectFilter(folder: VirtualFolder): void {
    this.selectedFilter = folder;
    this.stateService.setVirtualFolder(folder);
  }

  protected handleSearch(): void {
    this.popoverClose.next();
  }

  protected clearSearch(): void {
    // Only the search terms: the per-app date/stitched filters belong to the filters menu and its pill.
    this.searchService.resetSearchTerms();
    // The transient "View Contacts" ids are part of those terms, so the mode they opened ends here —
    // at the owner, independently of whether the folder-menu directive is mounted to notice.
    this.dpService.clearIdsSearchMode();
  }

  protected handleCancel(): void {
    this.popoverClose.next();
  }

  /** "Add details" on the quick form: continue in the full form with what was typed. */
  protected handleNewNotebook(notebook: Stitch): void {
    this.modalService.openFullForm(notebook, this.injector);
  }
}
